import type { Label } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import debounce from 'debounce-promise';
import React from 'react';
import { FormProvider, useController, useForm } from 'react-hook-form';
import { FaCheck, FaEllipsisH, FaRegTrashAlt } from 'react-icons/fa';
import { HookedInput } from '../../components/forms/HookedInput';
import { HookedTextArea } from '../../components/forms/HookedTextArea';
import { showModal } from '../../components/generic/Modal';
import { useUpdateLabelMutation } from '../../graphql/hooks/useUpdateLabelMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { LabelDeleteModal } from './LabelDeleteModal';

interface FormValues {
  name: string;
  description?: string | null;
  color: string;
}

interface Props {
  label: Label;
  onDelete?: (labelId: string) => void;
}

const HookedLabelColorItems = (props: { name: string }) => {
  const { name } = props;

  const {
    field: { ref, ...inputProps },
  } = useController({ name });

  const colors = ['blue', 'cyan', 'green', 'orange', 'pink', 'purple', 'red', 'teal', 'yellow'];

  return (
    <ChakraV3.List.Root display='inline-block' overflowY='auto' w='full'>
      {colors.map((item, index) => {
        return (
          <ChakraV3.ListItem
            key={`${item}${index}`}
            px={3}
            py={1}
            display='flex'
            flexDir='row'
            alignItems='center'
            _hover={{ bg: 'gray.100' }}
            onClick={() => inputProps.onChange(item)}
          >
            <ChakraV3.Tag colorPalette={item} size='md'>
              {P.upperFirst(item)}
            </ChakraV3.Tag>
            <ChakraV3.Spacer />
            {item === inputProps.value ? <FaCheck /> : null}
          </ChakraV3.ListItem>
        );
      })}
    </ChakraV3.List.Root>
  );
};

const LabelMenuContents = (props: Props) => {
  const { label, onDelete } = props;
  const { organization, errorToast } = useAppContext();
  const updateLabelMutation = useUpdateLabelMutation();

  const initialValues: FormValues = {
    name: label.name,
    description: label.description,
    color: label.color,
  };

  const methods = useForm({
    defaultValues: initialValues,
  });

  const watch = methods.watch;

  const autosave = debounce(async (values: Partial<FormValues>) => {
    const request = P.pickBy(values, (value, key) => {
      return !P.isEqual(value, initialValues[key as keyof FormValues]);
    });

    await updateLabelMutation
      .mutateAsync({
        organizationId: organization.id,
        labelId: label.id,
        request,
      })
      .catch((err: Error) => errorToast(`Error updating label: ${err.message}`));
  }, 1000);

  // Watch the form
  React.useEffect(() => {
    const subscription = watch(autosave);
    return () => subscription.unsubscribe();
  }, [watch, autosave]);

  return (
    <ChakraV3.Stack
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <FormProvider {...methods}>
        <ChakraV3.Menu.ItemGroup title='Name' color='gray.500'>
          <ChakraV3.Menu.ItemGroupLabel>Name</ChakraV3.Menu.ItemGroupLabel>
          <ChakraV3.Stack px={3} pt={1} pb={2} color='black'>
            <HookedInput name='name' fontWeight='normal' />
          </ChakraV3.Stack>
        </ChakraV3.Menu.ItemGroup>
        <ChakraV3.Menu.ItemGroup title='Description' color='gray.500'>
          <ChakraV3.Menu.ItemGroupLabel>Description</ChakraV3.Menu.ItemGroupLabel>
          <ChakraV3.Stack px={3} pt={1} pb={2} color='black'>
            <HookedTextArea
              name='description'
              fontSize='sm'
              py={3 / 2}
              px={3}
              minH='2lh'
              fontWeight='normal'
            />
          </ChakraV3.Stack>
        </ChakraV3.Menu.ItemGroup>
        <ChakraV3.Menu.Separator />
        <ChakraV3.Menu.Item
          onClick={() =>
            showModal(LabelDeleteModal, { labelId: label.id, labelName: label.name, onDelete })
          }
          fontSize='sm'
          value='delete'
          color='danger.solid'
        >
          <FaRegTrashAlt />
          <ChakraV3.Text>Delete</ChakraV3.Text>
        </ChakraV3.Menu.Item>
        <ChakraV3.Menu.Separator />
        <ChakraV3.Menu.ItemGroup title='Color' color='gray.500'>
          <ChakraV3.Menu.ItemGroupLabel>Color</ChakraV3.Menu.ItemGroupLabel>
          <HookedLabelColorItems name='color' />
        </ChakraV3.Menu.ItemGroup>
      </FormProvider>
    </ChakraV3.Stack>
  );
};

export const ManageLabelMenu = (props: Props) => {
  const { label, onDelete } = props;

  return (
    <ChakraV3.Menu.Root positioning={{ strategy: 'fixed' }} lazyMount unmountOnExit={false}>
      <ChakraV3.Menu.Trigger asChild>
        <ChakraV3.Button
          variant='ghost'
          _hover={{ bg: 'gray.200' }}
          _active={{ bg: 'gray.200' }}
          size='2xs'
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <FaEllipsisH />
        </ChakraV3.Button>
      </ChakraV3.Menu.Trigger>
      <ChakraV3.Menu.Positioner>
        <ChakraV3.Menu.Content zIndex={1900}>
          <LabelMenuContents label={label} onDelete={onDelete} />
        </ChakraV3.Menu.Content>
      </ChakraV3.Menu.Positioner>
    </ChakraV3.Menu.Root>
  );
};
